import React, { useEffect, useState } from "react";
import { getRates, updateRates } from "../../lib/api";
import { useRates } from "../../context/RatesContext";
import { DEFAULT_RATES } from "../../lib/costEngine";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { SlidersHorizontal, Loader2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";

const GROUPS = [
  {
    title: "Fixed budget",
    fields: [
      { key: "baseFixed", label: "Base fixed cost", kind: "currency" },
      { key: "includedMembers", label: "Members included in base", kind: "int" },
      { key: "blockSize", label: "Members per adjustment block", kind: "int" },
      { key: "blockCost", label: "Cost per adjustment block", kind: "currency" },
      { key: "contingencyPct", label: "Contingency", kind: "percent" },
    ],
  },
  {
    title: "Variable budget (per unit)",
    fields: [
      { key: "noticeMailUnit", label: "Notice mail-out / voter", kind: "currency" },
      { key: "ballotMailUnit", label: "Ballot mail-out / voter", kind: "currency" },
      { key: "internationalUnit", label: "International voter mail-out", kind: "currency" },
      { key: "additionalPollUnit", label: "Additional polling location", kind: "currency" },
      { key: "travelMarkup", label: "Travel markup", kind: "percent" },
    ],
  },
  {
    title: "Election-type reductions & tax",
    fields: [
      { key: "byElectionReduction", label: "By-election reduction", kind: "percent" },
      { key: "byElectionFloor", label: "By-election floor", kind: "currency" },
      { key: "refRatReduction", label: "Referendum / Ratification reduction", kind: "percent" },
      { key: "refRatFloor", label: "Referendum / Ratification floor", kind: "currency" },
      { key: "taxRate", label: "GST / Tax rate", kind: "percent" },
    ],
  },
];

const ALL_FIELDS = GROUPS.flatMap((g) => g.fields);

const toForm = (rates) => {
  const f = {};
  for (const { key, kind } of ALL_FIELDS) {
    const v = rates[key] ?? DEFAULT_RATES[key] ?? 0;
    f[key] = kind === "percent" ? String(+(Number(v) * 100).toFixed(4)) : String(v);
  }
  return f;
};

const toPayload = (form) => {
  const p = {};
  for (const { key, kind } of ALL_FIELDS) {
    const raw = Number(form[key]);
    const n = Number.isFinite(raw) ? raw : 0;
    if (kind === "percent") p[key] = +(n / 100).toFixed(6);
    else if (kind === "int") p[key] = Math.round(n);
    else p[key] = n;
  }
  return p;
};

// Guard against blank/invalid inputs silently becoming 0, and keep percentages in a sane 0–100 range.
const validateForm = (form) => {
  const errs = {};
  for (const { key, label, kind } of ALL_FIELDS) {
    const raw = form[key];
    if (raw === "" || raw == null) {
      errs[key] = `${label} cannot be empty.`;
      continue;
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      errs[key] = `${label} must be a valid number.`;
      continue;
    }
    if (n < 0) {
      errs[key] = `${label} cannot be negative.`;
      continue;
    }
    if (kind === "percent" && n > 100) {
      errs[key] = `${label} must be between 0 and 100%.`;
      continue;
    }
    // blockSize is a divisor in the cost engine; guard against 0.
    if (key === "blockSize" && n < 1) {
      errs[key] = `${label} must be at least 1.`;
    }
  }
  return errs;
};

export function RateSettingsCard() {
  const { refresh } = useRates();
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await getRates();
        setForm(toForm({ ...DEFAULT_RATES, ...r }));
      } catch (e) {
        setForm(toForm(DEFAULT_RATES));
      }
    })();
  }, []);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const handleSave = async () => {
    const errs = validateForm(form);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      toast.error("Please fix the highlighted rate fields before saving.");
      return;
    }
    setBusy(true);
    try {
      const saved = await updateRates(toPayload(form));
      setForm(toForm(saved));
      setErrors({});
      await refresh();
      toast.success("Rate card updated. New estimates will use these rates.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save rates.");
    } finally {
      setBusy(false);
    }
  };

  const handleReset = () => {
    setForm(toForm(DEFAULT_RATES));
    setErrors({});
  };

  return (
    <Card className="mb-8" data-testid="rate-settings-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between gap-2 mb-5">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-primary" />
            <p className="font-display font-600 text-foreground">Rate settings</p>
            <span className="text-xs text-muted-foreground">Configure the cost estimate rate card.</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleReset} disabled={busy || !form} data-testid="reset-rates-btn" className="gap-1.5">
              <RotateCcw className="h-3.5 w-3.5" /> Reset to defaults
            </Button>
            <Button size="sm" onClick={handleSave} disabled={busy || !form} data-testid="save-rates-btn" className="gap-1.5">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save rates
            </Button>
          </div>
        </div>

        {!form ? (
          <div className="flex items-center gap-2 text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading rate card…
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {GROUPS.map((group) => (
              <div key={group.title} className="space-y-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{group.title}</p>
                {group.fields.map(({ key, label, kind }) => (
                  <div key={key} className="space-y-1">
                    <label htmlFor={`rate-${key}`} className="text-sm text-foreground">
                      {label} {kind === "percent" ? "(%)" : kind === "currency" ? "($)" : ""}
                    </label>
                    <Input
                      id={`rate-${key}`}
                      data-testid={`rate-input-${key}`}
                      type="number"
                      step={kind === "int" ? "1" : "0.01"}
                      value={form[key]}
                      onChange={(e) => setField(key, e.target.value)}
                      className={`h-9 tabular-nums ${errors[key] ? "border-destructive focus-visible:ring-destructive" : ""}`}
                    />
                    {errors[key] && (
                      <p data-testid={`rate-error-${key}`} className="text-xs text-destructive">{errors[key]}</p>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
